import { BookOpen, Bookmark, Coins, RefreshCw, type LucideIcon } from "lucide-react";
import type { PatronCategoryRulesInput, PatronCategoryType } from "@/api/types";
import { cn } from "@/lib/utils";
import { CATEGORY_TYPE_LABELS, describeRules } from "../../patronCategories";

/**
 * The patron card a category produces, composed live from the draft, with its
 * rules spelled out the way a patron would read them.
 */
export function LibraryCardPreview({
  name,
  code,
  type,
  active,
  rules,
}: {
  name: string;
  code: string;
  type: PatronCategoryType;
  active: boolean;
  /** Only the values that are currently valid */
  rules: Partial<PatronCategoryRulesInput>;
}) {
  const lines = describeRules(rules);
  const displayCode = code.trim().toUpperCase();

  return (
    <figure className="space-y-5">
      <div
        aria-hidden
        className={cn(
          "relative flex aspect-[1.586/1] w-full max-w-[22rem] flex-col justify-between overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 pl-7 shadow-[0_1px_2px_rgb(0_0_0/0.04),0_12px_32px_-12px_rgb(0_0_0/0.18)] motion-safe:transition-[filter,opacity] dark:border-gray-700 dark:bg-gray-800 dark:shadow-none",
          !active && "opacity-70 grayscale"
        )}
      >
        {/* Edge band: a solid rail for a person, a double rail for a group; the type is also spelled out */}
        <span
          className={cn(
            "absolute inset-y-0 left-0",
            type === "individual"
              ? "bg-primary w-2"
              : "w-2.5 border-x-[3px] border-gray-800 dark:border-gray-300"
          )}
        />

        <div className="flex items-start justify-between gap-3">
          <div className="leading-tight">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Lumen</p>
            <p className="text-[10px] tracking-[0.18em] text-gray-500 uppercase dark:text-gray-400">
              Carte de lecteur
            </p>
          </div>
          <span className="rounded-full border border-gray-200 px-2 py-0.5 text-[11px] text-gray-600 dark:border-gray-600 dark:text-gray-300">
            {active ? CATEGORY_TYPE_LABELS[type] : "Inactive"}
          </span>
        </div>

        <p
          className={cn(
            "line-clamp-2 text-xl leading-snug font-semibold tracking-tight break-words",
            name.trim() ? "text-gray-900 dark:text-white" : "text-gray-300 dark:text-gray-600"
          )}
        >
          {name.trim() || "Nom de la catégorie"}
        </p>

        <div className="flex items-end justify-between gap-3">
          <Barcode value={displayCode} />
          <span className="font-mono text-xs tracking-[0.2em] text-gray-500 tabular-nums dark:text-gray-400">
            {displayCode || "CODE"}
          </span>
        </div>
      </div>

      <figcaption>
        <p className="mb-3 text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400">
          Ce que permet cette carte
        </p>
        <ul className="space-y-2.5">
          <RuleLine icon={BookOpen} text={lines.loans} />
          <RuleLine icon={RefreshCw} text={lines.renewals} />
          <RuleLine icon={Bookmark} text={lines.reservations} />
          <RuleLine icon={Coins} text={lines.fines} />
        </ul>
      </figcaption>
    </figure>
  );
}

function RuleLine({ icon: Icon, text }: { icon: LucideIcon; text: string | null }) {
  return (
    <li className="flex items-start gap-2.5 text-sm">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
      {text ? (
        <span className="text-gray-700 dark:text-gray-200">{text}</span>
      ) : (
        <span className="text-gray-400 italic dark:text-gray-500">Valeur à corriger</span>
      )}
    </li>
  );
}

/**
 * A barcode-like strip derived from the code, so each category gets its own
 * pattern. Purely decorative: it does not encode a scannable symbology.
 */
function Barcode({ value }: { value: string }) {
  const source = value || "CODE";
  const bars: { x: number; width: number }[] = [];
  let x = 0;
  for (const char of source) {
    const n = char.charCodeAt(0);
    for (let i = 0; i < 4; i++) {
      const width = ((n >> (i * 2)) & 3) + 1;
      if (i % 2 === 0) bars.push({ x, width });
      x += width + 1;
    }
  }

  return (
    <svg
      viewBox={`0 0 ${String(Math.max(x, 1))} 24`}
      preserveAspectRatio="none"
      className="h-7 w-32 text-gray-800 dark:text-gray-200"
    >
      {bars.map((bar) => (
        <rect key={bar.x} x={bar.x} y={0} width={bar.width} height={24} fill="currentColor" />
      ))}
    </svg>
  );
}
